import { Trans, useLingui } from '@lingui/react/macro';

import Badge from '@/design_system/Badge';
import Button from '@/design_system/Button';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import IconBandage from '@/icons/Bandage.svg';
import IconValidation from '@/icons/Validation.svg';
import { TEMPORARY_ID_PREFIX, useUpdateAction } from '@/models/article';
import { computeDuplicateDefectNumber, useGetDefectName } from '@/models/defectType';
import { type ArticleActionWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { createBEMClasses } from '@/utils/classname';

import { getEditedFields } from './getEditedFields';

const { element } = createBEMClasses('actions-list');

export const ActionDefectsMobile = ({
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const { article } = useArticleContext();

  const getDefectName = useGetDefectName();

  const actionDefects = [
    ...article.snapshot.articleDefects,
    ...(article.previousSnapshot?.articleDefects ?? []),
  ].filter((defect) =>
    (action ?? previousAction!).articleActionToArticleDefects.find(
      ({ articleDefectId }) => articleDefectId === defect.id
    )
  );

  return actionDefects.map((defect) => {
    const duplicateDefectNumber = computeDuplicateDefectNumber(
      defect,
      article.snapshot.articleDefects
    );

    return (
      <Badge
        key={defect.id}
        hasBorder
        color="neutral"
        borderRadius="soft"
        size="large"
        style={
          getEditedFields({ action, previousAction }).isDefectsEdited
            ? { backgroundColor: 'var(--color-tertiary-100)' }
            : undefined
        }
      >
        <IconBandage />
        {getDefectName(defect)}
        {duplicateDefectNumber ? ` #${duplicateDefectNumber}` : ''}
      </Badge>
    );
  });
};

export const ActionDefectsInput = ({
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const { request, article, view } = useArticleContext();
  const getDefectName = useGetDefectName();
  const { t } = useLingui();

  const isEditable =
    view.services.actions.fields.associatedDefects.editable &&
    !!action &&
    !action.id.startsWith(TEMPORARY_ID_PREFIX);

  const { mutate: updateAction } = useUpdateAction({
    articleId: article.id,
    requestId: request.id,
  });

  const defectOptions = isEditable
    ? article.snapshot.articleDefects.filter((defect) => !defect.id.startsWith(TEMPORARY_ID_PREFIX))
    : [...article.snapshot.articleDefects, ...(article.previousSnapshot?.articleDefects ?? [])];

  const selectedOptions = defectOptions.filter(
    (option) =>
      !!((action ?? previousAction)?.articleActionToArticleDefects ?? []).find(
        ({ articleDefectId }) => articleDefectId === option.id
      )
  );

  return (
    <div
      className={element('card-defects', {
        edited: getEditedFields({ action, previousAction }).isDefectsEdited,
      })}
    >
      <InputSelect
        variant="select"
        isMulti
        label={
          <>
            <Trans id="article.form.actions.defects.label">Associated defects</Trans>
            {isEditable && <OptionalLabel />}
          </>
        }
        labelFontWeight="regular"
        placeholder={
          isEditable
            ? t({
                id: 'article.form.actions.defects.add-defects.label',
                message: 'Add defects...',
              })
            : t({
                id: 'article.form.actions.defects.no-defects.label',
                message: 'No defects associated',
              })
        }
        isSearchable={false}
        isDisabled={!isEditable}
        options={defectOptions}
        value={selectedOptions}
        getOptionValue={(defectOption) => defectOption.id}
        getOptionLabel={(defectOption) => {
          const duplicateDefectNumber = computeDuplicateDefectNumber(
            defectOption,
            article.snapshot.articleDefects
          );

          return `${getDefectName(defectOption)}${duplicateDefectNumber ? ` #${duplicateDefectNumber}` : ''}`;
        }}
        formatOptionLabel={(defectOption) => {
          const duplicateDefectNumber = computeDuplicateDefectNumber(
            defectOption,
            article.snapshot.articleDefects
          );

          const label = `${getDefectName(defectOption)}${duplicateDefectNumber ? ` #${duplicateDefectNumber}` : ''}`;

          return (
            <Stack row gap=".5em" alignItems="center">
              {view.defects.fields.brandResponsibility.enabled &&
                defectOption.brandResponsibility && (
                  <Tooltip
                    content={t({
                      id: 'defect-list.brand-responsibility.true',
                      message: 'Under brand responsibility',
                    })}
                  >
                    <Button variant="style-less" className="text-disabled">
                      <IconValidation className="color-primary-700" style={{ fontSize: '1rem' }} />
                    </Button>
                  </Tooltip>
                )}
              {label}
            </Stack>
          );
        }}
        onChange={(defectOptions) => {
          if (!action) {
            return;
          }

          updateAction({
            actionId: action.id,
            body: {
              defectIds: defectOptions.map((defectOption) => defectOption.id),
            },
          });
        }}
        onRemoveValue={(defectOptionId) => {
          if (!action) {
            return;
          }

          updateAction({
            actionId: action.id,
            body: {
              defectIds: selectedOptions
                .filter(({ id }) => defectOptionId !== id)
                .map(({ id }) => id),
            },
          });
        }}
      />
    </div>
  );
};
